import { Eye, PencilLine, Sparkles, Loader2, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { Button } from "../../../../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../../../../components/ui/tabs";
import MarkdownRenderer from "../../../../../components/agno/MarkdownRenderer";
import { useGenerateSkillContent } from "../../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import useSkillsStore from "../../../../../store/skills/skillsStore";

const SkillInstructionsEditor = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const editor = useSkillsStore((s) => s.editor);
  const setEditor = useSkillsStore((s) => s.setEditor);
  const isGenerating = useSkillsStore((s) => s.isGenerating);
  const setIsGenerating = useSkillsStore((s) => s.setIsGenerating);

  const [viewMode, setViewMode] = useState<"edit" | "preview">("edit");
  const [aiPromptOpen, setAiPromptOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const generateMutation = useGenerateSkillContent();

  const handleGenerate = useCallback(() => {
    if (!aiPrompt.trim() || !workspace?.id) return;
    setIsGenerating(true);
    generateMutation.mutate(
      {
        workspace_id: workspace.id,
        prompt: aiPrompt.trim(),
        existing_content: editor.instructionsContent || undefined,
      },
      {
        onSuccess: (data) => {
          const content = data?.content || "";
          if (editor.instructionsContent) {
            setEditor({
              instructionsContent: editor.instructionsContent + "\n\n" + content,
            });
          } else {
            setEditor({ instructionsContent: content });
          }
          setAiPrompt("");
          setAiPromptOpen(false);
          setIsGenerating(false);
        },
        onError: () => {
          setIsGenerating(false);
        },
      },
    );
  }, [aiPrompt, workspace?.id, editor.instructionsContent]);

  const handleImproveSelection = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !workspace?.id) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    if (start === end) return;

    const selectedText = editor.instructionsContent.slice(start, end);
    setIsGenerating(true);
    generateMutation.mutate(
      {
        workspace_id: workspace.id,
        prompt: `Improve and enhance the following skill instruction text. Keep the same intent but make it clearer, more specific, and better structured:\n\n${selectedText}`,
        existing_content: editor.instructionsContent,
      },
      {
        onSuccess: (data) => {
          const improved = data?.content || selectedText;
          const newContent =
            editor.instructionsContent.slice(0, start) +
            improved +
            editor.instructionsContent.slice(end);
          setEditor({ instructionsContent: newContent });
          setIsGenerating(false);
        },
        onError: () => {
          setIsGenerating(false);
        },
      },
    );
  }, [workspace?.id, editor.instructionsContent]);

  return (
    <div className="flex flex-col gap-3 flex-1">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-foreground">Instructions</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAiPromptOpen(!aiPromptOpen)}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            AI Assist
          </Button>
          <Tabs
            value={viewMode}
            onValueChange={(v) => setViewMode(v as "edit" | "preview")}
          >
            <TabsList>
              <TabsTrigger value="edit">
                <PencilLine className="h-3 w-3 mr-1" />
                Edit
              </TabsTrigger>
              <TabsTrigger value="preview">
                <Eye className="h-3 w-3 mr-1" />
                Preview
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* AI Prompt Popover */}
      {aiPromptOpen && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-foreground flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Describe what this skill should do
            </span>
            <button
              type="button"
              onClick={() => setAiPromptOpen(false)}
              className="rounded p-0.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex gap-2">
            <textarea
              placeholder="e.g. Analyze customer health scores and predict churn risk based on usage patterns, support tickets, and engagement metrics..."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={2}
              className="flex-1 rounded-[5px] border border-border-chip bg-card px-2 py-1.5 text-[13px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary resize-none"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  handleGenerate();
                }
              }}
            />
            <Button
              size="sm"
              onClick={handleGenerate}
              disabled={!aiPrompt.trim() || isGenerating}
              className="self-end"
            >
              {isGenerating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "Generate"
              )}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            Press Cmd+Enter to generate. Select text in the editor and click "AI Assist" to improve it.
          </p>
        </div>
      )}

      {/* Editor / Preview */}
      <div className="flex-1 min-h-[300px] rounded-lg border border-border bg-card">
        {viewMode === "edit" ? (
          <textarea
            ref={textareaRef}
            value={editor.instructionsContent}
            onChange={(e) =>
              setEditor({ instructionsContent: e.target.value })
            }
            placeholder="Write your skill instructions in markdown...&#10;&#10;# Skill Name&#10;&#10;## Objective&#10;Describe what this skill should accomplish.&#10;&#10;## Steps&#10;1. First, do this...&#10;2. Then, check that...&#10;3. Finally, respond with..."
            className="h-full w-full resize-none rounded-lg bg-transparent px-4 py-3 font-mono text-[13px] leading-relaxed text-foreground placeholder:text-muted-foreground/50 focus-visible:outline-none"
            spellCheck={false}
          />
        ) : (
          <div className="h-full overflow-auto px-4 py-3">
            {editor.instructionsContent ? (
              <MarkdownRenderer>
                {editor.instructionsContent}
              </MarkdownRenderer>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Nothing to preview. Write some instructions first.
              </p>
            )}
          </div>
        )}
      </div>

      {viewMode === "edit" && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            Supports Markdown formatting
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleImproveSelection}
            disabled={isGenerating}
          >
            <Sparkles className="h-3 w-3" />
            Improve Selected Text
          </Button>
        </div>
      )}
    </div>
  );
};

export default SkillInstructionsEditor;
